/* eslint-disable import/no-unresolved */
import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  SimpleGrid,
} from "@chakra-ui/react";
import { useIntl } from "@react-intl";
import { IItems } from "@the-volte/svc-core-sdk/lib/api/Cart/types";
import { ISelectedProductInput } from "@the-volte/svc-core-sdk/lib/helpers";
import { ProductDetails_product } from "@the-volte/svc-core-sdk/lib/queries/gqlTypes/ProductDetails";
import { add, addDays } from "date-fns";
import dayjs from "dayjs";
import { get, isEmpty } from "lodash";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { ComponentProps, ReactNode, useContext, useState } from "react";
import { generatePath } from "react-router";

import { useDatalayer } from "@app/providers/DatalayerProvider";
import { useTikTokPixelContext } from "@app/providers/TikTokPixelProvider";
import { Button, ButtonBoxLink, Icon } from "@components/atoms";
import { ShopContext } from "@components/legacy/ShopProvider/context";
import ProductVariantSelector from "@components/molecules/ProductVariantSelector/ProductVariantSelector";
import { InputDatePicker } from "@components/organisms/";
import { paths } from "@paths";
import { commonMessages } from "@temp/intl";
import { decodeBase64Id } from "@utils/base64";
import { getAttributeValue } from "@utils/getAttributeValue";

import { channelSlug } from "../../../constants";
import AfterPay from "../../../images/afterpay.svg";
import ClearPay from "../../../images/clearpay.svg";
import {
  canAddToCart,
  getAfterpayPrice,
  getProductPrice,
} from "./stockHelpers";
import * as S from "./styles";

interface IAddToCartSection {
  product?: ProductDetails_product | null;
  productVariants?: ProductDetails_product["variants"];
  productSeoDescription?: ProductDetails_product["seoDescription"];
  productAttributes?: ProductDetails_product["attributes"];
  name: string;
  designer: string;
  designerSlug: string;
  bookedDates: Date[];
  productPricing?: ProductDetails_product["pricing"];
  items: IItems;
  isAvailableForPurchase?: boolean | null;
  availableForPurchase: string | null;
  variantId: string;
  setVariantId(variantId: string): void;
  onSubmit({
    variantId,
    quantity,
    rentalPeriod,
    bookedRange,
  }: ISelectedProductInput): void;
  mobile?: boolean;
  removeItem: (variantId: string) => Promise<{
    pending: boolean;
  }>;
  productBookingsLoading: boolean;
  loaded?: boolean;
  isAvailableForRent: boolean;
  isSamedayEligible?: boolean;
  canPickupToday?: boolean;
}

const processProductVariants = (
  productVariants: IAddToCartSection["productVariants"],
  startDate: Date | null,
  bookedDates: Date[]
) => {
  if (
    !productVariants ||
    productVariants.length === 0 ||
    isEmpty(productVariants)
  )
    return [];

  const productVariantsModified = productVariants.map(
    ({
      id,
      sku,
      name,
      rentalDays,
      quantityAvailable,
      images,
      pricing,
      attributes,
    }: any) => {
      let variantEndDate: Date | null = null;
      let disabled = false;

      if (startDate) {
        variantEndDate = addDays(startDate, rentalDays - 1);

        const varientEndDateString = variantEndDate?.toString();

        disabled =
          typeof varientEndDateString === "string" &&
          bookedDates.some((date) => date.toString() === varientEndDateString);
      }

      if (rentalDays === 365) disabled = false;

      return {
        __typename: "ProductVariant",
        id,
        sku,
        name,
        rentalDays,
        quantityAvailable,
        images,
        pricing,
        attributes,
        disabled,
      };
    }
  );

  return productVariantsModified;
};

export const AddToCartSection: React.FC<IAddToCartSection> = ({
  availableForPurchase,
  isAvailableForPurchase,
  items,
  name,
  designer,
  designerSlug,
  bookedDates,
  product,
  productPricing,
  productVariants,
  productSeoDescription,
  productAttributes,
  onSubmit,
  setVariantId,
  variantId,
  mobile,
  removeItem,
  productBookingsLoading,
  loaded = false,
  isAvailableForRent,
  isSamedayEligible = false,
  canPickupToday = true,
}) => {
  const intl = useIntl();
  const { clientIpAddress, track } = useTikTokPixelContext();
  const { push } = useDatalayer();
  const { currency } = useContext(ShopContext);

  const [submitting, setSubmitting] = useState(false);

  const selectedVariant = productVariants?.find((pv) => pv?.id === variantId);

  const rentalPeriod = selectedVariant?.rentalDays;

  const preparationDays = product?.resource?.preparationDays;

  const disabledDaysSortedAsc = bookedDates.sort(
    (objA, objB) => Number(objA) - Number(objB)
  );

  const today = new Date();

  // This is for the date where you can book the item
  let latestDate = !isEmpty(bookedDates)
    ? disabledDaysSortedAsc[disabledDaysSortedAsc.length - 1]
    : add(today, { days: 1 });
  latestDate = add(latestDate, { days: preparationDays });

  // Reset the date to 12am
  latestDate.setHours(12, 0, 0, 0);

  const noPurchaseAvailable = !isAvailableForPurchase && !availableForPurchase;
  const purchaseAvailableDate =
    !isAvailableForPurchase &&
    availableForPurchase &&
    Date.parse(availableForPurchase);

  const outOfStock = Number(selectedVariant?.quantityAvailable) <= 0;

  const maximumShippingDays = get(
    product,
    "shippingMethods[0].maximumDeliveryDays",
    0
  );

  const minimumShippingDays = get(
    product,
    "shippingMethods[0].minimumDeliveryDays",
    0
  );

  const disableButton =
    // eslint-disable-next-line strict-null-checks/all
    !canAddToCart(items || [], !!isAvailableForPurchase, variantId, 1) ||
    outOfStock;

  const renderErrorMessage = (message: ReactNode, testingContextId: string) => (
    <S.ErrorMessage
      data-test="stockErrorMessage"
      data-test-id={testingContextId}
    >
      {message}
    </S.ErrorMessage>
  );

  const router = useRouter();
  const { deliveryDate } = router.query;

  let useDateFilter = false;

  const [startDate, setStartDate] = useState(
    deliveryDate && typeof deliveryDate === "string"
      ? new Date(deliveryDate)
      : null
  );

  if (deliveryDate) useDateFilter = true;
  startDate?.setHours(12, 0, 0, 0);

  if (
    deliveryDate &&
    new Date(deliveryDate.toString()).getTime() < new Date().getTime()
  )
    useDateFilter = false;

  if (useDateFilter && startDate && startDate.getTime() > latestDate.getTime())
    latestDate = startDate;

  const isLatestPastStartDate =
    startDate && latestDate.getTime() > startDate.getTime();

  const productVariantsModified = processProductVariants(
    productVariants,
    startDate,
    bookedDates
  );

  const localeDateString = startDate?.toLocaleDateString("en-GB");

  const productSize = React.useMemo(() => {
    let productSizeStr = "";
    const productSizeAttr =
      productAttributes &&
      productAttributes?.length > 0 &&
      productAttributes.find((attr) => attr.attribute.name === "Size");
    if (productSizeAttr && productSizeAttr?.values?.length > 0) {
      productSizeStr = productSizeAttr.values
        .map((value) => value?.name)
        .join(", ");
    }
    return productSizeStr;
  }, [productAttributes]);

  const hasIncompleteOrder = !submitting && items && items.length;

  const cartWarning = hasIncompleteOrder ? (
    <>
      {" "}
      <Alert status="error" paddingY="16px" paddingX="12px">
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            marginBottom="5px"
          >
            <S.AlertIcon>
              <AlertIcon />
            </S.AlertIcon>

            <AlertTitle>Only one item can be ordered at a time.</AlertTitle>
          </Box>

          <AlertDescription>
            {`Complete the order for the ${name} or remove it from your bag.`}
          </AlertDescription>
        </Box>
      </Alert>
      <ButtonBoxLink
        style={{
          fontSize: "1rem",
          lineHeight: "1.1rem",
          marginTop: "10px",
          marginBottom: "10px",
          width: "100%",
          height: "56px",
          gap: "5px",
          background: "black",
          color: "white",
        }}
        href={paths.checkout}
        testingContext="complete-current-order-btn"
      >
        <S.BagIcon>
          <Icon name="cart" size={24} viewboxX={32} viewboxY={32} />
        </S.BagIcon>
        COMPLETE ORDER
      </ButtonBoxLink>
      <Button
        style={{
          whiteSpace: "normal",
          height: "40px",
          padding: "8px",
          fontSize: "1rem",
          lineHeight: "1.1rem",
          textTransform: "unset",
          marginBottom: "10px",
          width: "100%",
          gap: "5px",
        }}
        testingContext="clearCartButton"
        onClick={() => removeItem(items[0].variant.id)}
      >
        <S.RemoveIcon>
          <Icon name="trash" size={24} viewboxX={32} viewboxY={32} />
        </S.RemoveIcon>
        REMOVE FROM BAG
      </Button>
    </>
  ) : (
    ""
  );

  const isRental = (rentalPeriod || 0) < 365;
  const pricePrefix = isRental ? `Rental Price ` : "Purchase Price ";

  let dateSelectText = "RENT NOW";

  if (outOfStock) dateSelectText = "OUT OF STOCK";

  if (!isRental) dateSelectText = "BUY NOW";

  const changeDateText = mobile
    ? `CHANGE DELIVERY DATE`
    : `Change delivery date`;
  const deliveryText = isRental
    ? `Renting for a delivery date of ${localeDateString}`
    : `Purchasing for a delivery date of ${latestDate.toLocaleDateString(
        "en-GB"
      )}`;

  const trackAddToCart = () => {
    if (product) {
      const decodedProductId = decodeBase64Id(product.id);
      const designerAttribute = product.attributes.find(
        (attr) => attr.attribute.name === "Designer"
      );
      const pricing = selectedVariant?.pricing?.price?.gross;
      const borrowerFee =
        selectedVariant?.pricing?.borrowerFee?.gross?.amount || 0;
      const price = (pricing?.amount || 0) + borrowerFee;
      const brand = designerAttribute?.values?.[0]?.name || "";

      track("AddToCart", {
        contents: [
          {
            content_id: decodedProductId,
            content_name: product.name,
            content_type: "product",
            price,
            brand,
            quantity: 1,
          },
        ],
        value: price,
        currency: pricing?.currency || "AUD",
        description: clientIpAddress,
      });
    }
  };

  const pushToDataLayer = () => {
    const selectedVariantPrice =
      selectedVariant?.pricing?.price?.gross?.amount ?? 0;
    const borrowerFee =
      selectedVariant?.pricing?.borrowerFee?.gross?.amount ?? 0;
    const totalPrice = selectedVariantPrice + borrowerFee;

    if (product && selectedVariant && !!totalPrice) {
      push({
        event: "add_to_cart",
        ecommerce: {
          currency,
          value: totalPrice,
          items: [
            {
              item_id: decodeBase64Id(product.id),
              item_brand: getAttributeValue("designer", product.attributes),
              item_category: product.category?.name ?? "",
              item_name: product.name,
              item_variant: `Size ${getAttributeValue(
                "size",
                product.attributes
              )}`,
              price: totalPrice,
              quantity: 1,
            },
          ],
        },
      });
    }
  };

  return (
    <S.AddToCartSelection>
      <S.DesignerHeader
        data-test="designer"
        href={`/designers/${
          designerSlug.endsWith("-hire")
            ? designerSlug
            : designerSlug.concat("-hire")
        }`}
      >
        {designer}
        <ChevronRightIcon />
      </S.DesignerHeader>
      <S.ProductNameHeader data-test="productName">{name}</S.ProductNameHeader>
      <S.ProductSizeHeader data-test="productSize">
        {`Size ${productSize}`}
      </S.ProductSizeHeader>
      {isAvailableForRent && (
        <>
          <S.ProductPricing>
            {pricePrefix}
            {!!productPricing &&
              getProductPrice(productPricing, selectedVariant?.pricing)}
          </S.ProductPricing>
          <S.AfterPay>
            or 4 payments of{" "}
            {!!productPricing &&
              getAfterpayPrice(productPricing, selectedVariant?.pricing)}{" "}
            with
            {channelSlug !== "volte-uk" ? (
              <Image
                src={AfterPay.src}
                width={90}
                height={25}
                alt="afterpay-logo"
              />
            ) : (
              <Box alignItems="center" display="flex" paddingX={1}>
                <Image
                  src={ClearPay.src}
                  width={85}
                  height={20}
                  alt="clearpay-logo"
                />
              </Box>
            )}
          </S.AfterPay>
        </>
      )}

      {useDateFilter && isRental && (
        <S.DeliveryDate>
          <Icon name="tick_circle" size={20} viewboxX={16} viewboxY={16} />
          {deliveryText}
        </S.DeliveryDate>
      )}

      {useDateFilter && !isRental && !isLatestPastStartDate && (
        <S.DeliveryDate>
          <Icon name="tick_circle" size={20} viewboxX={16} viewboxY={16} />
          {deliveryText}
        </S.DeliveryDate>
      )}

      {noPurchaseAvailable &&
        isAvailableForRent &&
        renderErrorMessage(
          intl.formatMessage(commonMessages.noPurchaseAvailable),
          "notAvailable"
        )}

      {!isAvailableForRent && (
        <p
          style={{
            textAlign: "left",
            marginBottom: "10px",
          }}
        >
          The owner of this item hasn&apos;t set a price yet, Click Enquire Now
          and they&apos;ll be sent a message, we&apos;ll then let you know when
          the item is available.
        </p>
      )}

      {purchaseAvailableDate &&
        renderErrorMessage(
          intl.formatMessage(commonMessages.purchaseAvailableOn),
          "timeRestrictedAvailability"
        )}

      {!mobile && (
        <>
          <S.ProductDescription data-test="productDescription">
            {productSeoDescription}
          </S.ProductDescription>
          {isAvailableForRent && !hasIncompleteOrder && (
            <>
              <ProductVariantSelector
                productVariants={
                  productVariantsModified as ComponentProps<
                    typeof ProductVariantSelector
                  >["productVariants"]
                }
                variantId={variantId}
                setVariantId={setVariantId}
              />

              {!productBookingsLoading && loaded && isAvailableForRent && (
                <S.VariantPicker>
                  <InputDatePicker
                    disabledDays={
                      [
                        ...bookedDates,
                        !isSamedayEligible
                          ? {
                              before: dayjs()
                                .startOf("date")
                                .add(2, "day")
                                .toDate(),
                            }
                          : canPickupToday
                          ? { before: dayjs().startOf("date").toDate() }
                          : {
                              before: dayjs()
                                .startOf("date")
                                .add(1, "day")
                                .toDate(),
                            },
                      ] as any
                    }
                    dateFilterStartDate={
                      useDateFilter ? (startDate as Date) : undefined
                    }
                    daysRange={rentalPeriod || 3}
                    disabled={!rentalPeriod || disableButton}
                    onSubmit={async ({ from, to }) => {
                      setSubmitting(true);
                      trackAddToCart();
                      pushToDataLayer();

                      await onSubmit({
                        variantId,
                        quantity: 1,
                        rentalPeriod,
                        bookedRange: { from, to },
                      } as ISelectedProductInput);
                    }}
                    defaultText={dateSelectText}
                    sleepTime={500}
                    isPurchase={!isRental}
                    preparationDays={preparationDays || 3}
                    maximumShippingDays={maximumShippingDays}
                    minimumShippingDays={minimumShippingDays}
                    samedayPickupEnabled={isSamedayEligible}
                    canPickupToday={canPickupToday}
                  />
                </S.VariantPicker>
              )}
            </>
          )}

          {!isAvailableForRent && (
            <Button
              bgColor="transparent"
              onClick={() => {
                router.push(
                  generatePath(paths.enquire, {
                    slug: product?.slug,
                  })
                );
              }}
              sx={{
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              ENQUIRE NOW
            </Button>
          )}

          {!hasIncompleteOrder &&
            deliveryDate &&
            useDateFilter &&
            isRental &&
            !productBookingsLoading && (
              <S.ChangeDatePicker>
                <InputDatePicker
                  disabledDays={
                    [
                      ...bookedDates,
                      !isSamedayEligible
                        ? {
                            before: dayjs()
                              .startOf("date")
                              .add(2, "day")
                              .toDate(),
                          }
                        : canPickupToday
                        ? { before: dayjs().startOf("date").toDate() }
                        : {
                            before: dayjs()
                              .startOf("date")
                              .add(1, "day")
                              .toDate(),
                          },
                    ] as any
                  }
                  daysRange={rentalPeriod || 3}
                  disabled={!rentalPeriod || disableButton}
                  onSubmit={({ from }) => {
                    setStartDate(from);
                  }}
                  defaultText={changeDateText}
                  sleepTime={500}
                  preparationDays={preparationDays || 3}
                  maximumShippingDays={maximumShippingDays}
                  minimumShippingDays={minimumShippingDays}
                  samedayPickupEnabled={isSamedayEligible}
                  canPickupToday={canPickupToday}
                />
              </S.ChangeDatePicker>
            )}
          {cartWarning}
        </>
      )}

      {mobile && (
        <>
          {!isAvailableForRent && (
            <Button
              bgColor="transparent"
              onClick={() => {
                window.location.href = generatePath(paths.enquire, {
                  slug: product?.slug,
                });
              }}
              sx={{
                fontSize: "1rem",
                fontWeight: "bold",
                width: "100%",
              }}
            >
              ENQUIRE NOW
            </Button>
          )}
          {isAvailableForRent && !hasIncompleteOrder && (
            <>
              <S.PickBoxes>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                  <Box paddingLeft={2}>
                    <ProductVariantSelector
                      productVariants={
                        productVariantsModified as ComponentProps<
                          typeof ProductVariantSelector
                        >["productVariants"]
                      }
                      variantId={variantId}
                      setVariantId={setVariantId}
                    />
                  </Box>
                </SimpleGrid>
              </S.PickBoxes>
              <Box width="100%">
                <S.RentButton>
                  {!productBookingsLoading && (
                    <InputDatePicker
                      disabledDays={
                        [
                          ...bookedDates,
                          !isSamedayEligible
                            ? {
                                before: dayjs()
                                  .startOf("date")
                                  .add(2, "day")
                                  .toDate(),
                              }
                            : canPickupToday
                            ? { before: dayjs().startOf("date").toDate() }
                            : {
                                before: dayjs()
                                  .startOf("date")
                                  .add(1, "day")
                                  .toDate(),
                              },
                        ] as any
                      }
                      dateFilterStartDate={
                        useDateFilter ? (startDate as Date) : undefined
                      }
                      daysRange={rentalPeriod || 3}
                      disabled={!rentalPeriod || disableButton}
                      onSubmit={async ({ from, to }) => {
                        setSubmitting(true);
                        trackAddToCart();
                        pushToDataLayer();

                        await onSubmit({
                          variantId,
                          quantity: 1,
                          rentalPeriod,
                          bookedRange: { from, to },
                        } as ISelectedProductInput);
                      }}
                      defaultText={dateSelectText}
                      sleepTime={500}
                      isPurchase={!isRental}
                      preparationDays={preparationDays || 3}
                      maximumShippingDays={maximumShippingDays}
                      minimumShippingDays={minimumShippingDays}
                      samedayPickupEnabled={isSamedayEligible}
                      canPickupToday={canPickupToday}
                    />
                  )}
                </S.RentButton>
                <S.RentButton>
                  {!hasIncompleteOrder &&
                    deliveryDate &&
                    useDateFilter &&
                    isRental &&
                    !productBookingsLoading && (
                      <Box display="flex" marginTop="16px">
                        <InputDatePicker
                          disabledDays={
                            [
                              ...bookedDates,
                              !isSamedayEligible
                                ? {
                                    before: dayjs()
                                      .startOf("date")
                                      .add(2, "day")
                                      .toDate(),
                                  }
                                : canPickupToday
                                ? { before: dayjs().startOf("date").toDate() }
                                : {
                                    before: dayjs()
                                      .startOf("date")
                                      .add(1, "day")
                                      .toDate(),
                                  },
                            ] as any
                          }
                          daysRange={rentalPeriod || 3}
                          disabled={!rentalPeriod}
                          onSubmit={async ({ from, to }) => {
                            setSubmitting(true);
                            trackAddToCart();
                            pushToDataLayer();

                            await onSubmit({
                              variantId,
                              quantity: 1,
                              rentalPeriod,
                              bookedRange: { from, to },
                            } as ISelectedProductInput);
                          }}
                          defaultText={changeDateText}
                          sleepTime={500}
                          preparationDays={preparationDays || 3}
                          maximumShippingDays={maximumShippingDays}
                          minimumShippingDays={minimumShippingDays}
                          samedayPickupEnabled={isSamedayEligible}
                          canPickupToday={canPickupToday}
                        />
                      </Box>
                    )}
                </S.RentButton>
              </Box>
            </>
          )}
          {cartWarning}
          <br />
        </>
      )}
    </S.AddToCartSelection>
  );
};
